import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { OBJECTIVE_TYPE_SCOPES } from '../../utils/models/Objective';
import { getContrastColor } from '../../utils/util/color';
import { useEscape, useOutsideClick } from '../../utils/util/hooks';
import {
  filterValidParentTypes,
  isNoneTypeValid,
  ObjectiveType,
} from './ObjectiveTypeOperations';

interface Props {
  target: React.RefObject<HTMLElement>;
  isOpen: boolean;
  toggle: () => void;
  onClose: () => void;
  currentTypeId: number | null | undefined;
  onChangeType: (typeId: number | null) => void;
  userEditableObjectiveTypes: ObjectiveType[];
  organizationObjectiveTypes: ObjectiveType[];
  childrenTypeIds: (number | null | undefined)[];
  parentTypeId: number | null | undefined;
}

/**
 * Compute valid type options (including optional "None") for this objective,
 * considering the parent type and the types of its existing children.
 */
export const useValidTypeOptions = ({
  userEditableObjectiveTypes,
  organizationObjectiveTypes,
  childrenTypeIds,
  parentTypeId,
}: Pick<
  Props,
  | 'userEditableObjectiveTypes'
  | 'organizationObjectiveTypes'
  | 'childrenTypeIds'
  | 'parentTypeId'
>) => {
  const editableNonIndividualTypes = useMemo(
    () =>
      userEditableObjectiveTypes.filter(
        (t) => t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
      ),
    [userEditableObjectiveTypes]
  );

  const afterParentFilter = useMemo(
    () =>
      editableNonIndividualTypes.filter(
        (t) =>
          parentTypeId == null || t.link_parent_types?.includes(parentTypeId)
      ),
    [editableNonIndividualTypes, parentTypeId]
  );

  const validTypes = useMemo(
    () =>
      filterValidParentTypes(
        afterParentFilter,
        childrenTypeIds,
        organizationObjectiveTypes
      ),
    [afterParentFilter, childrenTypeIds, organizationObjectiveTypes]
  );

  const canSetNone = useMemo(
    () =>
      isNoneTypeValid(
        parentTypeId,
        childrenTypeIds,
        organizationObjectiveTypes
      ),
    [parentTypeId, childrenTypeIds, organizationObjectiveTypes]
  );

  const restrictedByParent =
    editableNonIndividualTypes.length > afterParentFilter.length;
  const restrictedByChildren = afterParentFilter.length > validTypes.length;

  return { validTypes, canSetNone, restrictedByParent, restrictedByChildren };
};

const ObjectiveTypePopover: FC<Props> = ({
  target,
  isOpen,
  toggle,
  onClose,
  currentTypeId,
  onChangeType,
  userEditableObjectiveTypes,
  organizationObjectiveTypes,
  childrenTypeIds,
  parentTypeId,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  const { validTypes, canSetNone, restrictedByParent, restrictedByChildren } =
    useValidTypeOptions({
      userEditableObjectiveTypes,
      organizationObjectiveTypes,
      childrenTypeIds,
      parentTypeId,
    });

  const isLockedToCurrent =
    !canSetNone &&
    validTypes.length === 1 &&
    validTypes[0].id === currentTypeId;

  useEscape(() => onClose());
  useOutsideClick(popoverRef, () => toggle());

  const handleSelect = (typeId: number | null) => {
    onChangeType(typeId);
    onClose();
  };

  const typeOptions = useMemo(
    () =>
      validTypes.map((ot) => {
        const isSelected = ot.id === currentTypeId;
        const style: React.CSSProperties = {
          height: '2rem',
          ...(ot.color
            ? isSelected
              ? {
                  backgroundColor: ot.color,
                  color: getContrastColor(ot.color),
                  borderColor: ot.color,
                }
              : { borderLeft: `4px solid ${ot.color}` }
            : {}),
        };
        return { ot, isSelected, style };
      }),
    [validTypes, currentTypeId]
  );

  return (
    <UncontrolledPopover
      target={target}
      delay={0}
      placement="bottom"
      isOpen={isOpen}
      toggle={toggle}
    >
      <div ref={popoverRef} role="dialog">
        <div className="fw-bold mb-1">
          <FormattedMessage
            id="app.views.objectives.objective_type_popover.title"
            defaultMessage="Change type"
          />
        </div>
        <div className="d-flex flex-column gap-2">
          {!canSetNone && typeOptions.length === 0 && (
            <div className="small text-muted">
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.no_options"
                defaultMessage="No other types available."
              />
            </div>
          )}
          {typeOptions.map(({ ot, isSelected, style }) => (
            <button
              key={ot.id}
              type="button"
              onClick={() => handleSelect(ot.id)}
              style={style}
              className={
                'btn btn-sm text-start d-flex align-items-center text-truncate ' +
                (isSelected ? '' : 'btn-white')
              }
            >
              <span className="text-truncate">
                <span className="fw-bold">{ot.name}</span>
                {ot.description && (
                  <span className={isSelected ? 'opacity-75' : 'text-muted'}>
                    {' '}
                    – {ot.description}
                  </span>
                )}
              </span>
            </button>
          ))}
          {canSetNone && (
            <button
              type="button"
              onClick={() => handleSelect(null)}
              style={{ height: '2rem' }}
              className={
                'btn btn-sm btn-white text-start d-flex align-items-center text-truncate ' +
                (!currentTypeId ? 'fw-bold' : '')
              }
            >
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.type_none"
                defaultMessage="Individual"
              />
            </button>
          )}
        </div>
        {isLockedToCurrent && (
          <div className="small text-muted mt-2">
            {restrictedByParent && restrictedByChildren ? (
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.locked_parent_and_children"
                defaultMessage="This is the only type compatible with this objective's parent and children."
              />
            ) : restrictedByParent ? (
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.locked_parent"
                defaultMessage="This is the only type compatible with this objective's parent."
              />
            ) : restrictedByChildren ? (
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.locked_children"
                defaultMessage="This is the only type compatible with this objective's children."
              />
            ) : (
              <FormattedMessage
                id="app.views.objectives.objective_type_popover.locked_no_alternatives"
                defaultMessage="This is the only type available to you."
              />
            )}
          </div>
        )}
      </div>
    </UncontrolledPopover>
  );
};

export default ObjectiveTypePopover;
